/**
 * Mode démonstration : le plugin répond depuis les jeux de données d'exemple
 * quand l'API du pipeline n'est pas encore servie.
 *
 * L'endpoint n'existe pas encore côté `front-dolibarr-agent` — le contrat donne
 * tous les endpoints comme « à écrire ». Plutôt qu'un panneau en erreur
 * permanente, le plugin montre à quoi ressemblera l'outil, avec les données
 * d'exemple du contrat.
 *
 * Ce mode est **annoncé en permanence** dans l'interface. Une synthèse
 * d'exemple prise pour l'état réel d'un dossier serait exactement le risque que
 * ce plugin est censé écarter.
 */

import * as cas from './cas.mjs';
import type { CasErreur } from './cas.mjs';

export type ModeDemo = 'auto' | 'toujours' | 'jamais';

const MODE: ModeDemo = (() => {
  const valeur = import.meta.env.VITE_MODE_DEMO;
  return valeur === 'toujours' || valeur === 'jamais' || valeur === 'auto' ? valeur : 'auto';
})();

export const modeDemo = MODE;

/** `toujours` : aucune requête réseau n'est même tentée. */
export const demoExclusif = MODE === 'toujours';

/** `jamais` : aucun repli, une API absente reste une erreur affichée. */
export const demoDesactive = MODE === 'jamais';

function estErreur(valeur: unknown): valeur is CasErreur {
  return typeof valeur === 'object' && valeur !== null && 'erreur' in valeur;
}

export type ReponseDemo =
  | { trouve: true; valeur: unknown }
  | { trouve: true; statut: number; corps: unknown }
  | { trouve: false };

/**
 * Rejoue un chemin de l'API v1 sur les données d'exemple.
 * `trouve: false` signale un chemin que la démonstration ne couvre pas :
 * l'appelant remonte alors l'erreur réseau d'origine, sans rien inventer.
 */
export function resoudreDemo(chemin: string): ReponseDemo {
  const [avantQuery, query = ''] = chemin.split('?');
  const segments = (avantQuery ?? '').split('/').filter(Boolean).map(decodeURIComponent);
  const [ressource, ...reste] = segments;

  const rendre = (valeur: unknown): ReponseDemo =>
    estErreur(valeur)
      ? { trouve: true, statut: valeur.erreur.statut, corps: valeur.erreur.corps }
      : { trouve: true, valeur };

  if (ressource === 'sante') return rendre(cas.sante());

  if (ressource === 'conversation' && reste[1] === 'contexte' && reste[0]) {
    return rendre(cas.contexteConversation(reste[0]));
  }

  if (ressource === 'client' && reste.length === 1 && reste[0]) {
    return rendre(cas.ficheClient(reste[0]));
  }

  if (ressource === 'todo' && reste.length === 1 && reste[0]) {
    return rendre(cas.todo(reste[0]));
  }

  if (ressource === 'relances' && reste.length === 1 && reste[0]) {
    return rendre(cas.relances(reste[0]));
  }

  if (ressource === 'recherche') {
    const parametres = new URLSearchParams(query);
    const limite = Number(parametres.get('limite') ?? 20);
    return rendre(cas.rechercher(parametres.get('q') ?? '', Number.isFinite(limite) ? limite : 20));
  }

  return { trouve: false };
}

/* ------------------------------------------------ état, pour la bannière */

let actif = MODE === 'toujours';
const auditeurs = new Set<() => void>();

/** Signale que le plugin sert des données d'exemple, et prévient l'interface. */
export function activerDemo(): void {
  if (actif) return;
  actif = true;
  for (const auditeur of auditeurs) auditeur();
}

export function demoEstActif(): boolean {
  return actif;
}

export function suivreDemo(auditeur: () => void): () => void {
  auditeurs.add(auditeur);
  return () => auditeurs.delete(auditeur);
}
